import { useEffect, useState } from "react";
import BaseCard from "../../../components/ui/BaseCard";
import { useQuery } from "@tanstack/react-query";
import BaseSwitch from "../../../components/ui/BaseSwitch";
import BaseDragDrop from "../../../components/ui/BaseDragDrop";
import BaseButton from "../../../components/ui/BaseButton";
import { useDispatch, useSelector } from "react-redux";
import {
  removeCustomerFile,
  updateWorkOrderForm,
  clearCustomerFiles,
} from "../../../store/workOrder/workOrderSlice";
import { getFileIconClass, isImage } from "../../../utils/helpers";
import DocumentLibraryModal from "../CreateWorkOrder/DocumentLibraryModal";
import { fetchUserWorkOrdersDocuments } from "../../../services/api/workOrder";

const DocumentsForm = ({ isEditMode, workOrderCustomerFiles, onHydrate }) => {
  const customerFiles = useSelector((state) => state.workOrder.summary.customerFiles);
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const dispatch = useDispatch();
  const [isSelectionRules, setIsSelectionRules] = useState(true);
  const [customerFilesLength, setCustomerFilesLength] = useState(false);

  const {
    data,
    isLoading: isfetchUserWorkOrdersDocumentsLoading,
    isError,
  } = useQuery({
    queryKey: ["documentLibrary"],
    queryFn: fetchUserWorkOrdersDocuments,
  });
  useEffect(() => {
    if (isEditMode && workOrderCustomerFiles) {
      const filesArray = Array.isArray(workOrderCustomerFiles)
        ? workOrderCustomerFiles.map((file) => ({
            id: file.id,
            name: file.filename,
            url: file.url,
            isRemote: true,
          }))
        : workOrderCustomerFiles.files || [];

      const hydratedCustomerFiles = {
        files: filesArray,
        isCustomerFilesVisible: !!filesArray.length,
      };

      dispatch(
        updateWorkOrderForm({
          step: "summary",
          data: { customerFiles: hydratedCustomerFiles },
        }),
      );
      // Baseline for the edit modal's Save Changes button — see useSectionChanges.
      onHydrate?.(hydratedCustomerFiles);
    }
  }, [isEditMode, workOrderCustomerFiles, dispatch]);

  const handleDrop = (newFiles) => {
    const existingFiles = customerFiles?.files || [];

    const limitedNewFiles = newFiles.slice(0, 5);
    if (newFiles.length > 5) {
      setCustomerFilesLength(true);
    } else {
      setCustomerFilesLength(false);
    }

    dispatch(
      updateWorkOrderForm({
        step: "summary",
        data: {
          customerFiles: {
            ...customerFiles,
            files: [...existingFiles, ...limitedNewFiles],
          },
        },
      }),
    );
  };

  const handleRemoveFile = (index) => {
    dispatch(removeCustomerFile(index));
  };
  const handleSelectFromLibrary = (selectedFiles) => {
    const existingFiles = customerFiles?.files || [];

    const formattedFiles = selectedFiles.map((f) => ({
      name: f.fileName,
      url: f.url,
      id: f.id,
      isRemote: true,
    }));

    dispatch(
      updateWorkOrderForm({
        step: "summary",
        data: {
          customerFiles: {
            ...customerFiles,
            files: [...existingFiles, ...formattedFiles],
          },
        },
      }),
    );
  };

  return (
    <BaseCard className="py-4 lg:px-16 px-5 mt-2 rounded-lg" elevation={1}>
      <div className="border-bottom mb-4 flex items-center">
        <h6 className="mb-2 text-dark">
          Customer Files <span className="italic text-[#686868] text-base">(Optional)</span>
        </h6>
        <BaseSwitch
          className="ml-4 mb-2"
          secondary={true}
          checked={isSelectionRules}
          onChange={() => setIsSelectionRules(!isSelectionRules)}
        />
      </div>

      {isSelectionRules && (
        <div className="grid grid-cols-2 gap-x-8 gap-y-4 py-3">
          <div className="lg:col-span-1 col-span-2">
            <BaseDragDrop onDrop={handleDrop}>
              <div className="flex items-center">
                <BaseButton
                  textSize="text-sm"
                  variant="outlined"
                  className="shadow-none w-28"
                  style={{ padding: "8px 20px" }}
                >
                  Add File
                </BaseButton>
                <p className="ml-8 sm:block hidden">or drag and drop here to upload</p>
              </div>
            </BaseDragDrop>
            <p className=" ml-1">(Max 5 at a time!)</p>
            {customerFilesLength && <p className="text-red-500">Only first 5 files uploaded</p>}

            <div className="flex my-5">
              <BaseSwitch
                secondary
                checked={customerFiles?.isCustomerFilesVisible}
                onChange={() =>
                  dispatch(
                    updateWorkOrderForm({
                      step: "summary",
                      data: {
                        customerFiles: {
                          isCustomerFilesVisible: !customerFiles?.isCustomerFilesVisible,
                        },
                      },
                    }),
                  )
                }
              />
              <p className="text-xs ml-2">
                supplier can view these files even before the work assignement
              </p>
            </div>
          </div>
          <div className="lg:col-span-1 col-span-2">
            <div className="bg-[#F8F9FC] border-2 border-dashed border-[#C4CAD4] rounded-md w-full min-h-[60px] flex items-center justify-center px-6 py-[14px] cursor-pointer">
              <div className="flex items-center">
                <BaseButton
                  textSize="text-sm"
                  variant="outlined"
                  className="shadow-none w-36"
                  style={{ padding: "8px 12px" }}
                  onClick={() => setIsLibraryOpen(true)}
                >
                  Document library
                </BaseButton>
                <p className="ml-8 sm:block hidden">find a file you already uploaded</p>
              </div>
            </div>
          </div>
          {customerFiles?.files?.map((file, index) => {
            const isRemoteFile = typeof file === "object" && "url" in file;

            const filePreviewUrl = isRemoteFile
              ? file.url // from backend
              : URL.createObjectURL(file); // local File

            return (
              // Full width until lg, or a tile sharing the row with the drop
              // zones is too narrow to hold a file name.
              <div className="bg-[#F6F6F6] rounded-md col-span-2 lg:col-span-1" key={index}>
                <div className="flex justify-between items-center gap-3 px-4 py-2">
                  {/* min-w-0 lets the name shrink instead of forcing the row
                      wider than the tile, which wrapped it one letter per line. */}
                  <div className="flex items-center min-w-0">
                    {isImage(file.name) ? (
                      <img
                        className="h-14 w-14 rounded-md shrink-0 object-cover"
                        src={filePreviewUrl}
                        alt={file.name}
                      />
                    ) : (
                      <div className="h-14 w-14 rounded-md bg-gray-100 shrink-0 flex items-center justify-center">
                        <i className={`fa-solid ${getFileIconClass(file.name)} text-[40px]`} />
                      </div>
                    )}
                    <div className="ml-3 min-w-0">
                      <p className="font-normal truncate" title={file?.name}>
                        {file?.name}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center shrink-0">
                    <i
                      className="fa-solid fa-eye cursor-pointer"
                      title="View File"
                      onClick={() => window.open(filePreviewUrl, "_blank")}
                    ></i>
                    <i
                      className="fa-regular fa-trash-can ml-3 cursor-pointer"
                      onClick={() => handleRemoveFile(index)}
                    ></i>
                  </div>
                </div>
              </div>
            );
          })}
          <DocumentLibraryModal
            open={isLibraryOpen}
            close={() => setIsLibraryOpen(false)}
            onSelect={handleSelectFromLibrary}
            data={data}
            isfetchUserWorkOrdersDocumentsLoading={isfetchUserWorkOrdersDocumentsLoading}
            isError={isError}
          />
        </div>
      )}
    </BaseCard>
  );
};

export default DocumentsForm;
